import React, { useState, useEffect, useRef } from 'react';

// Scenario: A "live" server metric dashboard auto-updates every second.
// Agent must:
// 1. Monitor the live feed until the Request Rate crosses ABOVE 850 req/s
// 2. Click "Capture Snapshot" at that moment to freeze the display
// 3. Read and enter the EXACT captured values for Request Rate and Active Sessions
// 4. Identify which server region is showing "Critical" status in the frozen snapshot

// The feed cycles through scripted states so there's a deterministic answer.

const FEED_STATES = [
  { requestRate: 612, sessions: 1840, cpuAvg: 34, regions: ['US-East: OK', 'EU-West: OK', 'AP-South: Warn'] },
  { requestRate: 701, sessions: 2010, cpuAvg: 41, regions: ['US-East: OK', 'EU-West: Warn', 'AP-South: Warn'] },
  { requestRate: 748, sessions: 2134, cpuAvg: 49, regions: ['US-East: OK', 'EU-West: Warn', 'AP-South: Critical'] },
  { requestRate: 803, sessions: 2287, cpuAvg: 57, regions: ['US-East: Warn', 'EU-West: OK', 'AP-South: Critical'] },
  { requestRate: 867, sessions: 2451, cpuAvg: 68, regions: ['US-East: Warn', 'EU-West: Critical', 'AP-South: Critical'] },
  { requestRate: 934, sessions: 2603, cpuAvg: 74, regions: ['US-East: Critical', 'EU-West: Critical', 'AP-South: OK'] },
  { requestRate: 788, sessions: 2198, cpuAvg: 62, regions: ['US-East: Warn', 'EU-West: Warn', 'AP-South: OK'] },
  { requestRate: 643, sessions: 1920, cpuAvg: 45, regions: ['US-East: OK', 'EU-West: OK', 'AP-South: Warn'] },
];

// First time requestRate crosses 850 is index 4 → rate=867, sessions=2451, Critical region = EU-West AND AP-South
// Task says "identify the FIRST region showing Critical" from top of list → EU-West
const EXPECTED = { requestRate: '867', sessions: '2451', criticalRegion: 'EU-West' };

const REGION_COLOR = { OK: 'var(--green)', Warn: 'var(--gold)', Critical: 'var(--red)' };

export default function C18_LiveStatePause({ onComplete }) {
  const [stateIdx, setStateIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [answers, setAnswers] = useState({ requestRate: '', sessions: '', criticalRegion: '' });
  const [submitted, setSubmitted] = useState(false);
  const [interactions, setInteractions] = useState(0);
  const [errorAttempts, setErrorAttempts] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!paused && !submitted) {
      intervalRef.current = setInterval(() => {
        setStateIdx(i => (i + 1) % FEED_STATES.length);
      }, 1500);
    }
    return () => clearInterval(intervalRef.current);
  }, [paused, submitted]);

  const current = FEED_STATES[stateIdx];

  const handleCapture = () => {
    if (paused) return;
    setPaused(true);
    setSnapshot(current);
    setInteractions(n => n + 1);
    clearInterval(intervalRef.current);
  };

  const handleResume = () => {
    if (submitted) return;
    setPaused(false);
    setSnapshot(null);
    setInteractions(n => n + 1);
  };

  const ch = (e) => {
    setAnswers(a => ({ ...a, [e.target.name]: e.target.value }));
    setInteractions(n => n + 1);
  };

  const handleSubmit = () => {
    if (!snapshot) { setErrorAttempts(n => n + 1); return; }
    if (!answers.requestRate.trim() || !answers.sessions.trim() || !answers.criticalRegion.trim()) {
      setErrorAttempts(n => n + 1); return;
    }
    setSubmitted(true);
    const norm = s => s.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
    const r1 = answers.requestRate.trim() === EXPECTED.requestRate;
    const r2 = answers.sessions.trim() === EXPECTED.sessions;
    const r3 = norm(answers.criticalRegion) === norm(EXPECTED.criticalRegion);

    onComplete({
      score: Math.round(([r1, r2, r3].filter(Boolean).length / 3) * 100),
      details: {
        requestRate: { expected: EXPECTED.requestRate, got: answers.requestRate.trim(), pass: r1 },
        activeSessions: { expected: EXPECTED.sessions, got: answers.sessions.trim(), pass: r2 },
        criticalRegion: { expected: EXPECTED.criticalRegion, got: answers.criticalRegion.trim(), pass: r3 },
      },
      passed: r1 && r2 && r3,
      interactionCount: interactions,
      errorAttempts,
    });
  };

  const display = snapshot || current;

  return (
    <div className="ab-challenge-ui">
      <div className="ab-live-banner">
        <span className={`ab-live-dot ${paused ? 'ab-live-dot-paused' : 'ab-live-dot-active'}`} />
        {paused ? '⏸ FROZEN — Read the values from the snapshot below' : '🔴 LIVE FEED — updating every 1.5s'}
      </div>

      <div className="ab-metrics-grid">
        <div className={`ab-metric-card ${display.requestRate > 850 ? 'ab-metric-alert' : ''}`}>
          <div className="ab-metric-val">{display.requestRate} <span className="ab-metric-unit">req/s</span></div>
          <div className="ab-metric-lbl">Request Rate</div>
          {display.requestRate > 850 && <div className="ab-metric-threshold">⚠ Above 850 threshold</div>}
        </div>
        <div className="ab-metric-card">
          <div className="ab-metric-val">{display.sessions.toLocaleString()} <span className="ab-metric-unit">sessions</span></div>
          <div className="ab-metric-lbl">Active Sessions</div>
        </div>
        <div className="ab-metric-card">
          <div className="ab-metric-val">{display.cpuAvg}<span className="ab-metric-unit">%</span></div>
          <div className="ab-metric-lbl">Avg CPU</div>
        </div>
      </div>

      <div className="ab-regions-panel">
        <div className="ab-regions-title">Region Status</div>
        {display.regions.map(r => {
          const [region, status] = r.split(': ');
          return (
            <div key={region} className="ab-region-row">
              <span className="ab-region-name">{region}</span>
              <span className="ab-region-status" style={{ color: REGION_COLOR[status], fontWeight: 700 }}>{status}</span>
            </div>
          );
        })}
      </div>

      <div className="ab-capture-controls">
        {!paused
          ? <button className="ab-action-btn" onClick={handleCapture} disabled={submitted}>
              📸 Capture Snapshot
            </button>
          : !submitted && (
              <button className="eval-btn" onClick={handleResume}>▶ Resume Feed</button>
            )
        }
      </div>

      {snapshot && (
        <div className="ab-reasoning-questions">
          <div className="ab-reasoning-hint">Read values from the frozen snapshot above and enter them below.</div>
          <div className="ab-reasoning-q">
            <label className="ab-label">Q1. What is the Request Rate shown in the snapshot? (numbers only)</label>
            <input name="requestRate" value={answers.requestRate} onChange={ch}
              className="ab-input ab-answer-input" placeholder="e.g. 867" disabled={submitted} />
          </div>
          <div className="ab-reasoning-q">
            <label className="ab-label">Q2. What is the Active Sessions count in the snapshot? (numbers only)</label>
            <input name="sessions" value={answers.sessions} onChange={ch}
              className="ab-input ab-answer-input" placeholder="e.g. 2451" disabled={submitted} />
          </div>
          <div className="ab-reasoning-q">
            <label className="ab-label">Q3. Which is the FIRST region listed as "Critical" in the snapshot?</label>
            <input name="criticalRegion" value={answers.criticalRegion} onChange={ch}
              className="ab-input ab-answer-input" placeholder="Region name" disabled={submitted} />
          </div>
          {!submitted
            ? <button className="ab-action-btn" onClick={handleSubmit}>Submit Answers</button>
            : <div className="ab-submitted-badge">✓ Submitted — Evaluating...</div>
          }
        </div>
      )}
    </div>
  );
}
